import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const PersonalInfoTab = ({ pilgrim, onUpdate }) => {
  const { t, currentLanguage } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: pilgrim?.firstName,
    lastName: pilgrim?.lastName,
    arabicFirstName: pilgrim?.arabicFirstName,
    arabicLastName: pilgrim?.arabicLastName,
    dateOfBirth: pilgrim?.dateOfBirth,
    placeOfBirth: pilgrim?.placeOfBirth,
    gender: pilgrim?.gender,
    nationality: pilgrim?.nationality,
    nationalId: pilgrim?.nationalId,
    passportNumber: pilgrim?.passportNumber,
    passportIssueDate: pilgrim?.passportIssueDate,
    passportExpiryDate: pilgrim?.passportExpiryDate,
    passportIssuePlace: pilgrim?.passportIssuePlace,
    email: pilgrim?.email,
    phone: pilgrim?.phone,
    whatsapp: pilgrim?.whatsapp,
    address: pilgrim?.address,
    city: pilgrim?.city,
    postalCode: pilgrim?.postalCode,
    emergencyContactName: pilgrim?.emergencyContactName,
    emergencyContactRelation: pilgrim?.emergencyContactRelation,
    emergencyContactPhone: pilgrim?.emergencyContactPhone
  });

  const genderOptions = [
    { value: 'male', label: t('pilgrimProfile.male') },
    { value: 'female', label: t('pilgrimProfile.female') }
  ];

  const nationalityOptions = [
    { value: 'moroccan', label: t('pilgrimProfile.moroccan') }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      firstName: pilgrim?.firstName,
      lastName: pilgrim?.lastName,
      arabicFirstName: pilgrim?.arabicFirstName,
      arabicLastName: pilgrim?.arabicLastName,
      dateOfBirth: pilgrim?.dateOfBirth,
      placeOfBirth: pilgrim?.placeOfBirth,
      gender: pilgrim?.gender,
      nationality: pilgrim?.nationality,
      nationalId: pilgrim?.nationalId,
      passportNumber: pilgrim?.passportNumber,
      passportIssueDate: pilgrim?.passportIssueDate,
      passportExpiryDate: pilgrim?.passportExpiryDate,
      passportIssuePlace: pilgrim?.passportIssuePlace,
      email: pilgrim?.email,
      phone: pilgrim?.phone,
      whatsapp: pilgrim?.whatsapp,
      address: pilgrim?.address,
      city: pilgrim?.city,
      postalCode: pilgrim?.postalCode,
      emergencyContactName: pilgrim?.emergencyContactName,
      emergencyContactRelation: pilgrim?.emergencyContactRelation,
      emergencyContactPhone: pilgrim?.emergencyContactPhone
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">
          {t('pilgrimProfile.personalInfo')}
        </h3>
        {!isEditing ? (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            {t('pilgrimProfile.edit')}
          </Button>
        ) : (
          <div className="flex gap-2 md:gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              {t('pilgrimProfile.cancel')}
            </Button>
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              {t('pilgrimProfile.save')}
            </Button>
          </div>
        )}
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6 space-y-6 md:space-y-8">
        <div>
          <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
            <Icon name="User" size={20} className="text-primary" />
            {t('pilgrimProfile.identityDetails')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('pilgrimProfile.firstName')}
              type="text"
              value={formData?.firstName}
              onChange={(e) => handleInputChange('firstName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.lastName')}
              type="text"
              value={formData?.lastName}
              onChange={(e) => handleInputChange('lastName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.arabicFirstName')}
              type="text"
              value={formData?.arabicFirstName}
              onChange={(e) => handleInputChange('arabicFirstName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.arabicLastName')}
              type="text"
              value={formData?.arabicLastName}
              onChange={(e) => handleInputChange('arabicLastName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.dateOfBirth')}
              type="date"
              value={formData?.dateOfBirth}
              onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.placeOfBirth')}
              type="text"
              value={formData?.placeOfBirth}
              onChange={(e) => handleInputChange('placeOfBirth', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Select
              label={t('pilgrimProfile.gender')}
              options={genderOptions}
              value={formData?.gender}
              onChange={(value) => handleInputChange('gender', value)}
              disabled={!isEditing}
              required
            />
            <Select
              label={t('pilgrimProfile.nationality')}
              options={nationalityOptions}
              value={formData?.nationality}
              onChange={(value) => handleInputChange('nationality', value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.nationalId')}
              type="text"
              value={formData?.nationalId}
              onChange={(e) => handleInputChange('nationalId', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>
        </div>

        <div>
          <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            {t('pilgrimProfile.passportInfo')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('pilgrimProfile.passportNumber')}
              type="text"
              value={formData?.passportNumber}
              onChange={(e) => handleInputChange('passportNumber', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.passportIssueDate')}
              type="date"
              value={formData?.passportIssueDate}
              onChange={(e) => handleInputChange('passportIssueDate', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.passportExpiryDate')}
              type="date"
              value={formData?.passportExpiryDate}
              onChange={(e) => handleInputChange('passportExpiryDate', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.passportIssuePlace')}
              type="text"
              value={formData?.passportIssuePlace}
              onChange={(e) => handleInputChange('passportIssuePlace', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>
        </div>

        <div>
          <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
            <Icon name="Phone" size={20} className="text-primary" />
            {t('pilgrimProfile.contactInfo')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('pilgrimProfile.email')}
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.phone')}
              type="tel"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.whatsapp')}
              type="tel"
              value={formData?.whatsapp}
              onChange={(e) => handleInputChange('whatsapp', e?.target?.value)}
              disabled={!isEditing}
            />
            <Input
              label={t('pilgrimProfile.city')}
              type="text"
              value={formData?.city}
              onChange={(e) => handleInputChange('city', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.address')}
              type="text"
              value={formData?.address}
              onChange={(e) => handleInputChange('address', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.postalCode')}
              type="text"
              value={formData?.postalCode}
              onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div>
          <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
            <Icon name="AlertCircle" size={20} className="text-primary" />
            {t('pilgrimProfile.emergencyContact')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label={t('pilgrimProfile.emergencyContactName')}
              type="text"
              value={formData?.emergencyContactName}
              onChange={(e) => handleInputChange('emergencyContactName', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.emergencyContactRelation')}
              type="text"
              value={formData?.emergencyContactRelation}
              onChange={(e) => handleInputChange('emergencyContactRelation', e?.target?.value)}
              disabled={!isEditing}
              required
            />
            <Input
              label={t('pilgrimProfile.emergencyContactPhone')}
              type="tel"
              value={formData?.emergencyContactPhone}
              onChange={(e) => handleInputChange('emergencyContactPhone', e?.target?.value)}
              disabled={!isEditing}
              required
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoTab;